import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./categoryCarousel.scss";

const AUTO_ADVANCE_MS = 5000;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Coverflow-style category carousel: native CSS scroll-snap rather than a
// carousel library (the project has none, and MUI v6 doesn't ship one), so
// touch momentum and RTL come for free.
//
// Auto-advances every 5s and stops for good on the first user interaction —
// otherwise the timer yanks the track out from under the user's finger.
function CategoryCarousel({ items, initialIndex = Math.floor(items.length / 2) }) {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [paused, setPaused] = useState(false);
  // Read by the auto-advance interval without re-arming it on every scroll.
  const activeIndexRef = useRef(initialIndex);
  activeIndexRef.current = activeIndex;

  const scrollToIndex = (index, smooth = true) => {
    itemRefs.current[index]?.scrollIntoView({
      behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto",
      inline: "center",
      block: "nearest",
    });
  };

  // Centre the initial item without animating — the design's loaded state.
  useEffect(() => {
    scrollToIndex(initialIndex, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The active item is whichever one's centre sits closest to the track's
  // centre. Measured off getBoundingClientRect, so it needs no RTL special
  // casing (scrollLeft is negative in RTL) and it stays correct mid-scroll.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    // Seven read-only rect reads per scroll event — cheap enough to run inline,
    // and unlike rAF it still resolves in a backgrounded tab.
    const measure = () => {
      const trackRect = track.getBoundingClientRect();
      const trackCentre = trackRect.left + trackRect.width / 2;

      let nearest = 0;
      let nearestDistance = Infinity;
      itemRefs.current.forEach((el, index) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - trackCentre);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = index;
        }
      });
      setActiveIndex(nearest);
    };

    track.addEventListener("scroll", measure, { passive: true });
    return () => track.removeEventListener("scroll", measure);
  }, [items]);

  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;

    const id = setInterval(() => {
      // A backgrounded tab would otherwise burn through the whole list.
      if (document.hidden) return;
      scrollToIndex((activeIndexRef.current + 1) % items.length);
    }, AUTO_ADVANCE_MS);

    return () => clearInterval(id);
  }, [paused, items.length]);

  return (
    <div className="wlc-carousel">
      <div
        className="wlc-carousel-track"
        ref={trackRef}
        role="group"
        aria-roledescription="carousel"
        onPointerDown={() => setPaused(true)}
        onWheel={() => setPaused(true)}
      >
        {items.map((item, index) => (
          <div
            key={item.key}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            className={`wlc-carousel-item${index === activeIndex ? " wlc-carousel-item--active" : ""}`}
          >
            <img src={item.icon} alt="" className="wlc-carousel-icon" />
            <span className="wlc-carousel-label">{t(item.labelKey)}</span>
          </div>
        ))}
      </div>

      <div className="wlc-carousel-dots">
        {items.map((item, index) => (
          <button
            key={item.key}
            type="button"
            className={`wlc-carousel-dot${index === activeIndex ? " wlc-carousel-dot--active" : ""}`}
            aria-label={t(item.labelKey)}
            aria-current={index === activeIndex}
            onClick={() => {
              setPaused(true);
              scrollToIndex(index);
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default CategoryCarousel;
